import { createContext, useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { ToastContainer } from './ToastContainer'

export type ToastVariant = 'success' | 'error' | 'info'

export interface ToastEntry {
  id: string
  variant: ToastVariant
  message: string
}

interface ToastContextValue {
  toasts: ToastEntry[]
  push: (variant: ToastVariant, message: string) => void
  dismiss: (id: string) => void
}

export const ToastContext = createContext<ToastContextValue | null>(null)

const AUTO_DISMISS_MS = 5000

interface ToastProviderProps {
  children: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
  }, [])

  const push = useCallback(
    (variant: ToastVariant, message: string) => {
      const id = `${String(Date.now())}-${Math.random().toString(36).slice(2, 8)}`
      setToasts((prev) => [...prev, { id, variant, message }])
      const timer = setTimeout(() => {
        dismiss(id)
      }, AUTO_DISMISS_MS)
      timersRef.current.set(id, timer)
    },
    [dismiss],
  )

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, push, dismiss }),
    [toasts, push, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}
